// @flow

import ReactDOM from 'react-dom';
import { useState, useContext } from 'react';
import { CheckoutFail } from '@tectonics/analytics/own';
import { type PaymentSpec } from '@tectonics/prs/payment-spec';
import { type PrsSupport } from '@tectonics/prs/support';
import { type AppValues } from '@tectonics/app-values';
import { type IncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { type LookAndFeel } from '@tectonics/look-and-feel';
import { mapOutgoingPayment, PlaceOrderError, placeOrder, type PlaceOrderErrorPayload } from '@tectonics/prs/outgoing-payment';
import { type UserInputValidationErrors, type UserInputValidator, filterUserInputValidationErrors } from '@tectonics/prs/validation';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { useSubmitTriesCount, useInputErrors, watchSubmitTimeout } from '@tectonics/abw';
import { GooglePayCanceledError, GooglePayError } from '@tectonics/prs/google-pay';
import { FingerprintError } from '@tectonics/prs/fingerprint';
import { mapUserInputValidation } from './map-user-input-validation';
import { isUserInputError, isConcurrencyError } from './error-messages';
import { mapUserInputError } from './map-user-input-error';
import { mapServerError } from './map-server-error';
import { handleWarning, handleError } from '@tectonics/alert-handler';
import { ThreeDSecureError } from '@tectonics/prs/threedsecure';
import { ControllerContext } from './controller';

type SetUserInputValidationErrors = ((UserInputValidationErrors) => UserInputValidationErrors) => void;

type UseSubmitArgs = {|
  +lookAndFeel: LookAndFeel,
  +spec: PaymentSpec,
  +prsSupport: PrsSupport,
  +incomingPayment: IncomingPayment,
  +setIncomingPayment: (IncomingPayment) => void,
  +setSubmitError: (?PlaceOrderErrorPayload) => void,
  +appValues: AppValues,
  +setUserInputValidationErrors: SetUserInputValidationErrors,
  +validateUserInput: UserInputValidator,
  +userInputValues: UserInputValues,
|};

export function useSubmit({
  lookAndFeel,
  spec,
  prsSupport,
  incomingPayment,
  setIncomingPayment,
  setSubmitError,
  appValues,
  setUserInputValidationErrors,
  validateUserInput,
  userInputValues,
}: UseSubmitArgs): [boolean, (boolean, boolean) => Promise<bool>] {
  const registerSubmitTry = useSubmitTriesCount(appValues);
  const registerInputErrors = useInputErrors(appValues);
  const [submitting, setSubmitting] = useState(false);
  const { localTenancy } = useContext(ControllerContext);

  const handleSubmit = async (hasValidatedLAF: boolean, hasLAFValidationErrors: boolean): Promise<bool> => {
    setSubmitting(false);
    setSubmitError(null);

    const userInputValidationResult = mapUserInputValidation({ spec, prsSupport, validateUserInput, userInputValues });
    const userInputValidationErrors = filterUserInputValidationErrors(userInputValidationResult);
    const hasValidationErrors = userInputValidationErrors.length > 0;

    registerSubmitTry(hasValidationErrors);
    registerInputErrors({
      userInputValidationErrors,
      hasValidatedLAF,
      hasLAFValidationErrors,
    });

    if (hasValidationErrors) {
      if (ENV_CONSTANTS.ENABLE_OWN_ANALYTICS) {
        fetch(ENV_CONSTANTS.ANDES_BASE_URL + '/analytics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: new CheckoutFail({
            incomingPayment,
            lookAndFeel,
            userInputValues,
            userInputValidationResult,
          }).asJSON(),
        });
      }
      return false;
    }

    setSubmitting(true);
    try {
      const outgoingPayment = await mapOutgoingPayment(incomingPayment, userInputValues, appValues);
      const result = await watchSubmitTimeout(async () => {
        const newIncomingPayment = await placeOrder(outgoingPayment, localTenancy);
        ReactDOM.unstable_batchedUpdates(() => {
          setIncomingPayment(newIncomingPayment);
          setSubmitting(false);
        });
        return newIncomingPayment.status !== 'OP';
      });
      return result;
    } catch(error) {
      setSubmitting(false);

      if (error instanceof GooglePayCanceledError) {
        return true;
      }

      if (error instanceof GooglePayError) {
        const code = 'GOOGLE-PAY';
        setSubmitError({
          code,
          message: mapServerError(code, getCountry(incomingPayment)) || error.message,
        });
        return false;
      }

      if (error instanceof FingerprintError) {
        handleWarning(error);
        return false;
      }

      if (error instanceof ThreeDSecureError) {
        const code = 'THREEDSECURE';
        setSubmitError({
          code,
          message: mapServerError(code, getCountry(incomingPayment)) || error.message,
        });
        return false;
      }

      if (!(error instanceof PlaceOrderError)) {
        handleError(error);
        return false;
      }

      if (isConcurrencyError(error.payload.code)) {
        window.location.reload();
        return false;
      }

      if (isUserInputError(error.payload.code)) {
        const mappedUserInputError = mapUserInputError(error.payload.code, incomingPayment.country);
        const [name, message] = mappedUserInputError;
        setUserInputValidationErrors(results => ({
          ...results,
          [name]: {
            id: 'server-error',
            payload: {
              message: message || error.payload.message,
              code: error.payload.code,
            },
          },
        }));
      } else {
        setSubmitError({
          code: error.payload.code,
          message: mapServerError(error.payload.code, getCountry(incomingPayment)) || error.payload.message,
        });
      }

      return false;
    }
  };

  return [submitting, handleSubmit];
}
